import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CtaButton } from "@/components/ui/CtaButton";
import { usePager, Pager } from "@/components/ui/TablePager";
import {
  BookOpen, Plus, Trash2, Pencil, Layers, GraduationCap, Sparkles, Palette,
  Atom, Globe2, FlaskConical, Calculator, Music, Code2, Brush, Languages, ScrollText, X,
} from "lucide-react";

// Suggested colour palette for subject tiles
const SUBJECT_COLORS = [
  "#FFB400", "#7C3AED", "#EC4899", "#10B981", "#0EA5E9",
  "#F97316", "#EF4444", "#6366F1", "#14B8A6", "#A855F7",
];
const SUBJECT_ICONS = {
  "book-open": BookOpen,
  atom: Atom,
  globe: Globe2,
  flask: FlaskConical,
  calc: Calculator,
  music: Music,
  code: Code2,
  brush: Brush,
  lang: Languages,
  scroll: ScrollText,
};

/**
 * /teacher/curriculum — single page with two tabs (Subjects | Grades).
 * Subjects + grades are the foundation; every other AI tool can be scoped to a subject.
 */
export default function Curriculum() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("subjects");
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [showAddGrade, setShowAddGrade] = useState(false);
  const [editing, setEditing] = useState(null); // subject row in edit mode

  useEffect(() => { reload(); }, []);
  const reload = async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([api.get("/subjects"), api.get("/grades")]);
      setSubjects(s.data.subjects || []);
      setGrades(g.data.grades || []);
    } catch (e) { toast.error("Failed to load curriculum"); }
    finally { setLoading(false); }
  };

  const gradeOptions = useMemo(() => grades.map((g) => ({ id: g.id, name: g.name })), [grades]);

  const onDeleteSubject = async (id) => {
    if (!window.confirm("Delete this subject? Linked assets won't be deleted — just unlinked.")) return;
    try { await api.delete(`/subjects/${id}`); reload(); toast.success("Subject removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const onDeleteGrade = async (id) => {
    if (!window.confirm("Delete this grade? Subjects linked to it will be detached.")) return;
    try { await api.delete(`/grades/${id}`); reload(); toast.success("Grade removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const onBulkDeleteGrades = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} grade${ids.length > 1 ? "s" : ""}? Linked subjects are detached but kept.`)) return;
    const res = await Promise.allSettled(ids.map((id) => api.delete(`/grades/${id}`)));
    toast.success(`Deleted ${res.filter((r) => r.status === "fulfilled").length}`);
    reload();
  };

  return (
    <div className="space-y-6" data-testid="curriculum-page">
      {/* Heading */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ curriculum</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter">Subjects & Grades</h1>
          <p className="text-sm text-neutral-500 mt-1">Set up the curriculum once. Every quiz, lesson, recording &amp; resource you create gets organised here.</p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "subjects" ? (
            <CtaButton onClick={() => setShowAddSubject(true)} variant="primary" size="md" data-testid="add-subject-btn" className="rounded-full">
              <Plus size={14} /> Add subject
            </CtaButton>
          ) : (
            <CtaButton onClick={() => setShowAddGrade(true)} variant="primary" size="md" data-testid="add-grade-btn" className="rounded-full">
              <Plus size={14} /> Add grade
            </CtaButton>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex bg-white border border-black/8 rounded-full p-1 gap-1">
        <TabBtn active={tab === "subjects"} onClick={() => setTab("subjects")} testid="tab-subjects" icon={Layers} label={`Subjects (${subjects.length})`} />
        <TabBtn active={tab === "grades"} onClick={() => setTab("grades")} testid="tab-grades" icon={GraduationCap} label={`Grades (${grades.length})`} />
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-sm text-neutral-500">Loading curriculum…</div>
      ) : tab === "subjects" ? (
        subjects.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No subjects yet"
            sub="Add your first subject — e.g. Maths, Physics, History — and we'll spin up a dedicated workspace for each one."
            cta="Add subject"
            onCta={() => setShowAddSubject(true)}
          />
        ) : (
          <SubjectsTable
            subjects={subjects}
            grades={gradeOptions}
            onOpen={(s) => navigate(`/teacher/subjects/${s.id}`)}
            onDelete={onDeleteSubject}
            onEdit={(s) => setEditing(s)}
          />
        )
      ) : grades.length === 0 ? (
        <EmptyState
          icon={GraduationCap}
          title="No grades yet"
          sub="Add the grades / classes / levels you teach (e.g. Grade 8, O-Level, Class V). Then attach subjects to them."
          cta="Add grade"
          onCta={() => setShowAddGrade(true)}
        />
      ) : (
        <GradesTable grades={grades} onDelete={onDeleteGrade} onBulkDelete={onBulkDeleteGrades} subjectsCount={(gid) => subjects.filter((s) => s.grade_id === gid).length} />
      )}

      {showAddSubject && (
        <SubjectModal
          grades={gradeOptions}
          onClose={() => setShowAddSubject(false)}
          onSaved={() => { setShowAddSubject(false); reload(); }}
        />
      )}
      {editing && (
        <SubjectModal
          grades={gradeOptions}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {showAddGrade && (
        <GradeModal
          onClose={() => setShowAddGrade(false)}
          onSaved={() => { setShowAddGrade(false); reload(); }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, testid, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-1.5 text-xs font-mono uppercase tracking-[0.18em] rounded-full inline-flex items-center gap-2 transition ${active ? "bg-[#0A0C10] text-[#FBFAF7] shadow-sm" : "text-neutral-600 hover:text-[#0A0C10]"}`}
    ><Icon size={12} /> {label}</button>
  );
}

function EmptyState({ icon: Icon, title, sub, cta, onCta }) {
  return (
    <div className="bg-white border border-dashed border-neutral-300 rounded-2xl p-12 text-center" data-testid="curriculum-empty">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#FFD600] to-[#FFB400] flex items-center justify-center"><Icon size={24} className="text-[#0A0C10]" /></div>
      <h3 className="mt-4 font-display font-bold text-xl">{title}</h3>
      <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">{sub}</p>
      <CtaButton onClick={onCta} variant="primary" size="md" className="mt-5 rounded-full"><Plus size={14} /> {cta}</CtaButton>
    </div>
  );
}

function SubjectsTable({ subjects, grades, onOpen, onDelete, onEdit }) {
  const gradeName = (gid) => grades.find((g) => g.id === gid)?.name || "—";
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="subjects-grid">
      {subjects.map((s) => {
        const Icon = SUBJECT_ICONS[s.icon] || BookOpen;
        return (
          <div
            key={s.id}
            data-testid={`subject-card-${s.id}`}
            className="group bg-white border border-black/10 rounded-2xl p-5 hover:border-[#0A0C10] transition cursor-pointer relative overflow-hidden"
            onClick={() => onOpen(s)}
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-15" style={{ background: s.color }} />
            <div className="flex items-start justify-between relative">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${s.color}22`, color: s.color }}>
                <Icon size={22} />
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => onEdit(s)} data-testid={`subject-edit-${s.id}`} className="p-1.5 text-neutral-500 hover:text-[#0A0C10]"><Pencil size={13} /></button>
                <button onClick={() => onDelete(s.id)} data-testid={`subject-del-${s.id}`} className="p-1.5 text-red-500 hover:text-red-700"><Trash2 size={13} /></button>
              </div>
            </div>
            <div className="mt-3 font-display font-bold text-xl tracking-tighter">{s.name}</div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mt-1">{gradeName(s.grade_id)}</div>
            {s.description && <p className="text-xs text-neutral-500 mt-2 line-clamp-2">{s.description}</p>}
            <div className="mt-4 inline-flex items-center gap-1 text-xs text-[#0A0C10] font-semibold">
              Open workspace <Sparkles size={12} className="text-[#FFB400]" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GradesTable({ grades, onDelete, onBulkDelete, subjectsCount }) {
  const [selected, setSelected] = useState([]);
  const sorted = [...grades].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const { page, setPage, pages, pageItems, total } = usePager(sorted, 25);
  const allChecked = sorted.length > 0 && sorted.every((g) => selected.includes(g.id));
  const toggleAll = () => setSelected(allChecked ? [] : sorted.map((g) => g.id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="bg-white border border-black/8 rounded-xl overflow-hidden" data-testid="grades-table">
      {selected.length > 0 && (
        <div className="flex items-center justify-end px-4 py-2 border-b border-black/8">
          <button onClick={() => { onBulkDelete(selected); setSelected([]); }} data-testid="curriculum-grades-bulk-delete"
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-red-500/10 text-red-500 border border-red-400/40 hover:bg-red-500/20">
            <Trash2 size={12} /> Delete selected ({selected.length})
          </button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-[#FBFAF7] text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">
          <tr>
            <th className="px-4 py-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="curriculum-grades-select-all" /></th>
            <th className="text-left px-5 py-3">Grade</th>
            <th className="text-left px-5 py-3">Description</th>
            <th className="text-left px-5 py-3">Subjects</th>
            <th className="px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((g) => (
            <tr key={g.id} className="border-t border-black/5 hover:bg-[#FBFAF7]/50" data-testid={`grade-row-${g.id}`}>
              <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(g.id)} onChange={() => toggle(g.id)} data-testid={`curriculum-grade-check-${g.id}`} /></td>
              <td className="px-5 py-3 font-semibold">{g.name}</td>
              <td className="px-5 py-3 text-neutral-600">{g.description || "—"}</td>
              <td className="px-5 py-3 text-neutral-500">{subjectsCount(g.id)}</td>
              <td className="px-5 py-3 text-right">
                <button onClick={() => onDelete(g.id)} data-testid={`grade-del-${g.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={page} pages={pages} total={total} onPage={setPage} testid="curriculum-grades-pager" />
    </div>
  );
}

function SubjectModal({ grades, existing = null, onClose, onSaved }) {
  const [name, setName] = useState(existing?.name || "");
  const [gradeId, setGradeId] = useState(existing?.grade_id || "");
  const [color, setColor] = useState(existing?.color || SUBJECT_COLORS[0]);
  const [icon, setIcon] = useState(existing?.icon || "book-open");
  const [desc, setDesc] = useState(existing?.description || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Subject name required");
    setBusy(true);
    try {
      const body = { name: name.trim(), grade_id: gradeId, color, icon, description: desc.trim() };
      if (existing) await api.patch(`/subjects/${existing.id}`, body);
      else await api.post("/subjects", body);
      toast.success(existing ? "Subject updated" : "Subject added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={existing ? "Edit subject" : "Add subject"} onClose={onClose} testid="subject-modal">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name *">
          <input className={inp} required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Mathematics" data-testid="subject-name-input" />
        </Field>
        <Field label="Grade">
          <select className={inp} value={gradeId} onChange={(e) => setGradeId(e.target.value)} data-testid="subject-grade-select">
            <option value="">— Not linked —</option>
            {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
        <Field label="Colour">
          <div className="flex gap-1.5 flex-wrap">
            {SUBJECT_COLORS.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} title={c}
                className={`w-7 h-7 rounded-full ${color === c ? "ring-2 ring-offset-2 ring-[#0A0C10]" : ""}`}
                style={{ background: c }} data-testid={`color-${c.slice(1)}`} />
            ))}
          </div>
        </Field>
        <Field label="Icon">
          <div className="flex gap-1.5 flex-wrap">
            {Object.entries(SUBJECT_ICONS).map(([k, Icon]) => (
              <button key={k} type="button" onClick={() => setIcon(k)}
                className={`w-9 h-9 rounded-md border flex items-center justify-center ${icon === k ? "bg-[#0A0C10] text-white border-[#0A0C10]" : "border-neutral-200 hover:border-neutral-400 text-neutral-600"}`}
                data-testid={`icon-${k}`}>
                <Icon size={16} />
              </button>
            ))}
          </div>
        </Field>
        <Field label="Description (optional)">
          <textarea rows={3} className={inp} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Anything useful — syllabus board, key topics…" data-testid="subject-desc-input" />
        </Field>
        <CtaButton type="submit" disabled={busy} variant="primary" size="lg" className="w-full rounded-full" data-testid="subject-submit">
          <Plus size={14} /> {busy ? "Saving…" : (existing ? "Save changes" : "Create subject")}
        </CtaButton>
      </form>
    </Modal>
  );
}

function GradeModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Grade name required");
    setBusy(true);
    try {
      await api.post("/grades", { name: name.trim(), description: desc.trim() });
      toast.success("Grade added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Add grade" onClose={onClose} testid="grade-modal">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name *">
          <input className={inp} required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grade 9 / O-Level / Class V" data-testid="grade-name-input" />
        </Field>
        <Field label="Description (optional)">
          <textarea rows={3} className={inp} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Stage, board, age group…" data-testid="grade-desc-input" />
        </Field>
        <CtaButton type="submit" disabled={busy} variant="primary" size="lg" className="w-full rounded-full" data-testid="grade-submit">
          <Plus size={14} /> {busy ? "Saving…" : "Create grade"}
        </CtaButton>
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">/ curriculum</div>
            <h3 className="font-display font-bold text-xl">{title}</h3>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

const inp = "w-full bg-white text-sm border border-neutral-200 rounded-md px-3 py-2 outline-none focus:border-[#0A0C10]";
